import { differenceInMilliseconds, format, intlFormat } from 'date-fns'
import { startCase } from 'lodash'
import { IntlShape } from 'react-intl'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import Accordion from '@/components/Accordion/Accordion'
import { ArrowRight, CheckFilled, ErrorSmall } from '@/components/Icons'
import TruncatedJSONPretty from '@/pages/SpaceAppBuilder/components/ExecutionSteps/TruncatedJSONPretty'
import { StyledMessage } from '@/pages/SpaceAppBuilder/utilities/StyledComponents'

import messages from './messages'

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`

const StyledDateTimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  margin: 0 1rem 0 0.65rem;
`

const StyledStatus = styled.span`
  font-size: 1rem;
  margin-right: 0.6rem;
  color: ${(props: any) => (props.failed ? 'red' : 'green')};
`
const StyledSubtleText = styled.span`
  color: #8b8b8b;
  font-size: 13px;
`

const StyledDateTimeContainer = styled.div`
  width: 90%;
  align-items: center;
  display: flex;
  @media (max-width: 1050px) {
    display: none;
  }
`

const StyledErrorIconSmall = styled(ErrorSmall)`
  height: 20px;
  width: 20px;
  margin-right: 3%;
`

// TODO Enhance the Button to be a react-dom Link
const ButtonSizedLink = styled<any>(Link)`
  height: 44px;
  width: 44px;
  display: grid;
  align-content: center;
  justify-content: center;
  border-radius: 5px;

  &:hover,
  &:active,
  &:focus {
    background-color: #eaf0f6;
  }
`
const StyledAccordion = styled(Accordion)`
  max-width: 500px;
`
interface ColumnsProps {
  intl: IntlShape
}
interface RowData {
  rowData: {
    id: number
    status: string
    createdDate: string
    updatedDate: string
    errorMessage?: string
    executionDate: string
    debugLoggingEnabled?: boolean
  }
}

export default ({ intl }: ColumnsProps) => {
  const location = useLocation<{ fromAppDetailsPage: boolean }>()

  return [
    {
      property: 'createdDate',
      header: {
        label: intl.formatMessage(messages.dateTime),
        sortable: true
      },
      cell: {
        transforms: [
          (_name: string, { rowData }: RowData) => ({
            index: rowData.id
          })
        ],
        formatters: [
          (name: string, { rowData }: RowData) => {
            return (
              <div className="flex">
                <StyledDateTimeContainer>
                  <StyledDateTimeColumn data-testid="dateTime-block">
                    <StyledRow>
                      <StyledSubtleText>{`${
                        rowData.createdDate &&
                        intlFormat(new Date(rowData.createdDate), {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric'
                        })
                      }`}</StyledSubtleText>
                    </StyledRow>
                    <StyledSubtleText>
                      {`${intl.formatMessage(messages.duration)} ${
                        differenceInMilliseconds(new Date(rowData.updatedDate), new Date(rowData.createdDate)) / 1000
                      } ${intl.formatMessage(messages.seconds)}`}
                    </StyledSubtleText>
                  </StyledDateTimeColumn>
                  {rowData.debugLoggingEnabled ? (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                      Debug
                    </span>
                  ) : null}
                </StyledDateTimeContainer>
              </div>
            )
          }
        ]
      },
      props: {
        style: {
          maxWidth: 200,
          verticalAlign: 'top'
        }
      }
    },
    {
      property: 'condition',
      header: {
        label: intl.formatMessage(messages.condition)
      },
      cell: {
        transforms: [
          (_name: string, { rowData }: RowData) => ({
            index: rowData.id
          })
        ],
        formatters: [
          (name: string, { rowData }: RowData) => {
            const isJson = (str: string) => {
              try {
                JSON.parse(str)
              } catch {
                return false
              }
              return true
            }
            const renderStatus = () => (
              <StyledRow>
                {rowData.status === 'failed' ? (
                  <StyledErrorIconSmall className="error-icon" />
                ) : (
                  <CheckFilled style={{ marginRight: '3%', height: '20px', width: '20px' }} />
                )}
                {/*// @ts-expect-error*/}
                <StyledStatus failed={rowData.status === 'failed'}>{startCase(`${rowData.status}`)}</StyledStatus>
              </StyledRow>
            )

            return (
              <StyledRow>
                <StyledDateTimeContainer>
                  <StyledDateTimeColumn data-testid="condition-block">
                    <StyledRow>
                      {rowData.status === 'failed' && rowData.errorMessage ? (
                        <StyledAccordion title={renderStatus()} defaultOpen={true}>
                          <StyledMessage negative={true}>
                            {isJson(rowData.errorMessage) ? (
                              <TruncatedJSONPretty data={rowData.errorMessage} />
                            ) : (
                              rowData.errorMessage
                            )}
                          </StyledMessage>
                        </StyledAccordion>
                      ) : (
                        renderStatus()
                      )}
                    </StyledRow>
                  </StyledDateTimeColumn>
                </StyledDateTimeContainer>
              </StyledRow>
            )
          }
        ]
      },
      props: {
        style: {
          width: 500
        }
      }
    },
    {
      property: 'condition',
      header: {
        label: ''
      },
      cell: {
        formatters: [
          (_r: string, { rowData }: RowData) => {
            return (
              <div className="flex h-5">
                <ButtonSizedLink
                  to={{
                    pathname: `${location.pathname}/${rowData.id}/${rowData.executionDate}`,
                    state: location.state
                  }}
                >
                  <ArrowRight
                    title={intl.formatMessage(messages.viewDetailsHint, {
                      for: format(new Date(rowData.createdDate), 'Pp')
                    })}
                  />
                </ButtonSizedLink>
              </div>
            )
          }
        ]
      }
    }
  ]
}
